import { Icon, MenuItem } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { presentations as presentationsApi } from "apis-v2/callables";
import { GetSlidesMetadataResponse } from "apis-v2/presentations/handlers";
import getObjectHash from "common/utils/getObjectHash";
import { ActionPermissionsObject, IWorkspaceResourceFolder, isTeamResourceAllTeamMembersFolderOrSubfolder, PermissionActionType, WorkspaceResourceType } from "common/interfaces";
import { _ } from "js/vendor";

import UpgradeIcon from "js/Components/UpgradeIcon";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";
import { Gap5 } from "js/react/components/Gap";
import { ContextMenu } from "js/react/components/UiComponents";
import BulkActionBar, { BulkActionButton } from "js/react/views/SharedResources/Components/BulkActionBar";
import PresentationTemplatesPaneController, { PresentationTemplatesPaneControllerState } from "js/controllers/PresentationTemplatesPaneController";
import { t } from "js/i18n";
import { NoMatchNotice, NoTeamTemplateNotice, SharedSlideFolderNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { openTemplateSettingsDialog, PresentationTemplateItem, PresentationTemplateItemSkeleton } from "js/react/views/SharedResources/Components/PresentationTemplateItem";
import { mouseDragItems } from "js/react/views/SharedResources/utils/mouseDragHelper";
import { removeResourcesFromFolder } from "js/react/views/SharedResources/utils/removeResourcesFromFolder";

export type PresentationTemplateDragItem = {
    id: string;
    resourceId: string;
    resourceType: WorkspaceResourceType;
};

export interface PresentationTemplateListProps {
    editable: boolean;
    allowDragging?: boolean;
    selectedFolderId?: string | null;
    resourceFolders?: IWorkspaceResourceFolder[];
    onSelect?: (presentationId: string | null, isDoubleClick?: boolean) => void;
    onStartDragItems?: (items: PresentationTemplateDragItem[]) => void;
    onDropDragItems?: () => void;
    onNavigateToFolder?: (filter: { folderId: string; subFolderId?: string }) => void;
}

const ListContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0;
    flex: 1;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
    position: static;
`;

const Container = styled.div`
    width: 100%;
    min-width: 0;
`;

const logger = getLogger(LogGroup.TEAMS);

const toDragItem = (record: PresentationTemplatesPaneControllerState["data"][number]): PresentationTemplateDragItem => ({
    id: record.document.id,
    resourceId: record.resource.resourceId,
    resourceType: record.resource.resourceType,
});

function PresentationTemplateList({
    editable,
    allowDragging = false,
    selectedFolderId = null,
    resourceFolders = [],
    onSelect,
    onStartDragItems,
    onDropDragItems,
    onNavigateToFolder,
    data,
    filters,
    search,
    fetching,
    tags,
}: PresentationTemplateListProps & PresentationTemplatesPaneControllerState) {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const selectedFolder = resourceFolders.find(folder => folder.id === selectedFolderId);
    const parentFolder = resourceFolders.find(folder => folder.id === selectedFolder?.parentFolderId);
    const canRemoveFromSelectedFolder = !isTeamResourceAllTeamMembersFolderOrSubfolder(selectedFolder, parentFolder);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const presentationsSlidesMetadataRef = useRef<Record<string, Promise<GetSlidesMetadataResponse>>>({});
    const listRef = useRef<HTMLDivElement>(null);
    const startSelectionIndexRef = useRef(0);
    const dragStartedRef = useRef(false);
    const itemWasSelectedOnMouseDownRef = useRef(false);

    const selectedRecords = useMemo(
        () => data.filter(record => selectedIds.includes(record.document.id)),
        [data, selectedIds]
    );

    const hasPublished = selectedRecords.some(r => r.resource.metadata?.isPublished !== false);
    const hasUnpublished = selectedRecords.some(r => r.resource.metadata?.isPublished === false);

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const handleGetPresentationSlidesMetadata = useCallback(async (presentationId: string) => {
        if (!presentationsSlidesMetadataRef.current[presentationId]) {
            presentationsSlidesMetadataRef.current[presentationId] = presentationsApi.getSlidesMetadata({ id: presentationId });
        }
        return presentationsSlidesMetadataRef.current[presentationId];
    }, []);

    const handleSelect = useCallback((presentationId: string | null, isDoubleClick?: boolean) => {
        if (!editable) {
            setSelectedIds(presentationId ? [presentationId] : []);
        }
        onSelect?.(presentationId, isDoubleClick);
    }, [editable, onSelect]);

    const toDragItems = useCallback((ids: string[]) => {
        return ids.flatMap(id => {
            const record = data.find(item => item.document.id === id);
            return record ? [toDragItem(record)] : [];
        });
    }, [data]);

    const handleItemMouseDown = useCallback((
        record: PresentationTemplatesPaneControllerState["data"][number],
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (!allowDragging) {
            return;
        }

        event.stopPropagation();
        const presentationId = record.document.id;
        let nextSelection = [...selectedIds];
        itemWasSelectedOnMouseDownRef.current = nextSelection.includes(presentationId);

        if (event.metaKey || event.ctrlKey) {
            if (nextSelection.includes(presentationId)) {
                nextSelection = nextSelection.filter(id => id !== presentationId);
            } else {
                nextSelection = [...nextSelection, presentationId];
            }
        } else if (event.shiftKey && nextSelection.length) {
            const startIndex = startSelectionIndexRef.current;
            const endIndex = data.findIndex(item => item.document.id === presentationId);

            if (endIndex > startIndex) {
                for (let i = startIndex + 1; i <= endIndex; i++) {
                    const id = data[i].document.id;
                    if (!nextSelection.includes(id)) {
                        nextSelection.push(id);
                    }
                }
            } else {
                for (let i = startIndex; i >= endIndex; i--) {
                    const id = data[i].document.id;
                    if (!nextSelection.includes(id)) {
                        nextSelection.push(id);
                    }
                }
            }
        } else {
            if (!nextSelection.includes(presentationId)) {
                nextSelection = [presentationId];
                startSelectionIndexRef.current = data.findIndex(item => item.document.id === presentationId);
            }

            const dragSelection = toDragItems(nextSelection);
            const mousedItem = toDragItem(record);
            dragStartedRef.current = false;

            mouseDragItems({
                event,
                container: listRef.current,
                mousedItem,
                selection: dragSelection,
                onDidStartDrag: () => {
                    dragStartedRef.current = true;
                },
                onStartDragItems: () => onStartDragItems?.(dragSelection),
                onDropDragItems: () => onDropDragItems?.(),
            });
        }

        setSelectedIds(nextSelection);
    }, [allowDragging, data, onDropDragItems, onStartDragItems, selectedIds, toDragItems]);

    const handleItemMouseUp = useCallback((
        record: PresentationTemplatesPaneControllerState["data"][number],
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (!allowDragging) {
            return;
        }

        if (event.shiftKey || event.metaKey || event.ctrlKey) {
            return;
        }

        const presentationId = record.document.id;
        const didDrag = dragStartedRef.current;
        const itemWasSelectedOnMouseDown = itemWasSelectedOnMouseDownRef.current;

        event.preventDefault();

        setSelectedIds(current => {
            const isDeselect = itemWasSelectedOnMouseDown &&
                !didDrag &&
                current.length === 1 &&
                current[0] === presentationId;

            return isDeselect ? [] : [presentationId];
        });
    }, [allowDragging]);

    const handleListMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!allowDragging) {
            return;
        }

        if (
            (event.target as HTMLElement).closest("[data-item-id]") ||
            (event.target as HTMLElement).closest("[data-testid='bulk-action-bar']")
        ) {
            return;
        }

        setSelectedIds([]);
    }, [allowDragging]);

    const handleItemClick = useCallback((presentationId: string | null, isDoubleClick?: boolean) => {
        setSelectedIds(presentationId ? [presentationId] : []);
        onSelect?.(presentationId, isDoubleClick);
    }, [onSelect]);

    const handleItemMenuClick = useCallback((presentationId: string) => {
        setSelectedIds([presentationId]);
    }, []);

    const handleRemoveMultipleFromFolder = useCallback(async (presentationIds: string[]) => {
        if (!canRemoveFromSelectedFolder || !selectedFolderId || !presentationIds.length) {
            return;
        }

        const resources = toDragItems(presentationIds).map(({ resourceId, resourceType }) => ({
            resourceId,
            resourceType,
        }));

        await removeResourcesFromFolder({
            folderId: selectedFolderId,
            resources,
            resourceLabel: presentationIds.length === 1 ? t("template") : t("templates"),
        });
        setSelectedIds([]);
    }, [canRemoveFromSelectedFolder, selectedFolderId, toDragItems]);

    const handleRemoveFromFolder = useCallback(async (presentationId: string) => {
        const ids = selectedIds.includes(presentationId) && selectedIds.length > 0
            ? selectedIds
            : [presentationId];
        await handleRemoveMultipleFromFolder(ids);
    }, [handleRemoveMultipleFromFolder, selectedIds]);

    const handleBulkPublish = useCallback(async (publish: boolean, targetRecords?: PresentationTemplatesPaneControllerState["data"]) => {
        const records = targetRecords || selectedRecords;
        if (!records.length) {
            return;
        }

        const isSingle = records.length === 1;

        if (publish) {
            const confirmed = await ShowConfirmationDialog({
                title: isSingle
                    ? t("Are you sure you want to publish this Presentation Template to your team?")
                    : t("Are you sure you want to publish {{count}} selected templates to your team?", { count: records.length }),
                message: t("Your team members will be able to create a new presentation starting from this template by selecting it in Create New Presentation dialog."),
                okButtonLabel: isSingle ? t("Publish Template") : t("Publish {{count}} Templates", { count: records.length }),
            });

            if (!confirmed) {
                return;
            }
        }

        const recordsToUpdate = records.filter(r => (publish ? r.resource.metadata?.isPublished === false : r.resource.metadata?.isPublished !== false));
        if (!recordsToUpdate.length) {
            setSelectedIds([]);
            return;
        }

        await withProgressDialog({
            title: publish
                ? (isSingle ? t("Publishing template...") : t("Publishing {{count}} templates...", { count: records.length }))
                : (isSingle ? t("Unpublishing template...") : t("Unpublishing {{count}} templates...", { count: records.length })),
            action: async () => {
                await WorkspaceController.updateWorkspaceResources(recordsToUpdate.map(record => ({
                    resourceId: record.resource.resourceId,
                    resourceType: record.resource.resourceType,
                    metadata: {
                        isPublished: publish,
                    },
                })));
            },
        });
        setSelectedIds([]);
    }, [selectedRecords]);

    const handleDeleteTemplates = useCallback(async (targetRecords?: PresentationTemplatesPaneControllerState["data"]) => {
        const records = targetRecords || selectedRecords;
        if (!records.length) {
            return;
        }

        const isSingle = records.length === 1;
        const confirmed = await ShowConfirmationDialog({
            title: isSingle
                ? t("Permanently delete this Team Template?")
                : t("Permanently delete {{count}} selected Team Templates?", { count: records.length }),
            message: isSingle
                ? t("This cannot be undone. Existing presentations created from this template will remain unchanged.")
                : t("This cannot be undone. Existing presentations created from these templates will remain unchanged."),
            buttonOptions: { variant: "outlined", style: { color: "#FF4500", borderColor: "transparent" } },
            okButtonLabel: t("DELETE"),
        });

        if (confirmed) {
            await withProgressDialog({
                title: isSingle ? t("Deleting template...") : t("Deleting {{count}} templates...", { count: records.length }),
                action: async () => {
                    try {
                        await Promise.all(records.map(record =>
                            WorkspaceController.deleteWorkspaceResource({
                                resourceId: record.resource.resourceId,
                                resourceType: record.resource.resourceType,
                            })
                        ));
                    } catch (err) {
                        logger.error(err, "[PresentationTemplateList] Error deleting templates");
                        ShowErrorDialog({
                            title: t("Unable to delete template"),
                            message: err.message || t("An error occurred while deleting the template. Please try again."),
                        });
                    }
                },
            });
            setSelectedIds([]);
        }
    }, [selectedRecords]);

    const handleBulkAddTags = useCallback(async (targetRecords?: PresentationTemplatesPaneControllerState["data"]) => {
        const records = targetRecords || selectedRecords;
        if (!records.length) {
            return;
        }

        const existingTags = tags.map(tag => tag.tag);

        const count = records.length;
        const subTitle = count === 1
            ? t("Add tags to this resource to help your team find it faster. Existing tags will not be removed.")
            : t("Add tags to {{count}} selected resources to help your team find them faster. Existing tags will not be removed.", { count });

        ShowDialog(AddTagsDialog, {
            activeTags: [],
            existingTags,
            subTitle,
            acceptCallback: async (newTags: string[]) => {
                if (!newTags.length) {
                    return;
                }
                await withProgressDialog({
                    title: t("Updating tags..."),
                    action: async () => {
                        await WorkspaceController.updateWorkspaceResources(records.map(record => {
                            const existing = (record.resource.tags || []).map(t => _.trim(t)).filter(Boolean);
                            return {
                                resourceId: record.resource.resourceId,
                                resourceType: record.resource.resourceType,
                                tags: _.uniqBy([...existing, ...newTags], t => t.toLowerCase()),
                            };
                        }));
                    },
                });
                setSelectedIds([]);
            },
        });
    }, [selectedRecords, tags]);

    const handleDuplicateTemplate = useCallback(async (record: PresentationTemplatesPaneControllerState["data"][number]) => {
        try {
            await withProgressDialog({
                title: t("Duplicating template..."),
                action: async () => {
                    const { presentationData } = await presentationsApi.copyPresentation({
                        id: record.document.id,
                        workspaceId: record.resource.workspaceId,
                        name: record.document.name,
                    });
                    AppController.showEditor({
                        presentationId: presentationData.id,
                        folderId: selectedFolderId ?? undefined,
                    });
                },
            });
        } catch (err) {
            logger.error(err, "[PresentationTemplateList] Error duplicating template", { id: record.document.id });
            ShowErrorDialog({
                title: t("Unable to duplicate template"),
                message: err.message || t("An error occurred while duplicating the template. Please try again."),
            });
        }
    }, [selectedFolderId]);

    const handleEditTemplateSettings = useCallback((record: PresentationTemplatesPaneControllerState["data"][number]) => {
        openTemplateSettingsDialog({ record, tags, selectedFolderId });
    }, [selectedFolderId, tags]);

    const handleOpenPresentationEditor = useCallback((record: PresentationTemplatesPaneControllerState["data"][number]) => {
        AppController.showEditor({ presentationId: record.resource.resourceId, folderId: selectedFolderId });
    }, [selectedFolderId]);

    const handleItemContextMenu = useCallback((
        record: PresentationTemplatesPaneControllerState["data"][number],
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        if (!editable) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();

        const presentationId = record.document.id;
        if (!selectedIds.includes(presentationId)) {
            setSelectedIds([presentationId]);
        }
        setContextMenu({ mouseX: event.pageX, mouseY: event.pageY });
    }, [editable, selectedIds]);

    const renderContextMenuItems = () => {
        if (!contextMenu || !selectedRecords.length) {
            return null;
        }

        if (selectedRecords.length > 1) {
            const hasSelectionUnpublished = selectedRecords.some(r => r.resource.metadata?.isPublished === false);
            const hasSelectionPublished = selectedRecords.some(r => r.resource.metadata?.isPublished !== false);

            return [
                <MenuItem
                    key="add-tags"
                    divider
                    onClick={() => {
                        setContextMenu(null);
                        void handleBulkAddTags(selectedRecords);
                    }}
                >
                    <Icon>label</Icon>{t("Add tags…")}
                </MenuItem>,
                ...(hasSelectionUnpublished ? [
                    <MenuItem
                        key="publish"
                        divider={!hasSelectionPublished}
                        onClick={() => {
                            setContextMenu(null);
                            void handleBulkPublish(true, selectedRecords);
                        }}
                    >
                        <Icon>cloud_upload</Icon>{t("Publish Template")}
                    </MenuItem>,
                ] : []),
                ...(hasSelectionPublished ? [
                    <MenuItem
                        key="unpublish"
                        divider
                        onClick={() => {
                            setContextMenu(null);
                            void handleBulkPublish(false, selectedRecords);
                        }}
                    >
                        <Icon>unpublished</Icon>{t("Unpublish Template")}
                    </MenuItem>,
                ] : []),
                ...(canRemoveFromSelectedFolder ? [
                    <MenuItem
                        key="remove-from-folder"
                        divider
                        onClick={() => {
                            const ids = selectedRecords.map(r => r.document.id);
                            setContextMenu(null);
                            void handleRemoveMultipleFromFolder(ids);
                        }}
                    >
                        <Icon>remove_circle</Icon>{t("Remove from Folder")}
                    </MenuItem>,
                ] : []),
                <MenuItem
                    key="delete"
                    onClick={() => {
                        setContextMenu(null);
                        void handleDeleteTemplates(selectedRecords);
                    }}
                >
                    <Icon>delete</Icon>{t("Delete Template")}
                </MenuItem>,
            ];
        }

        const record = selectedRecords[0];
        const isPublished = record.resource.metadata?.isPublished !== false;

        return [
            ...(canUpgradeToManageSharedResources ? [
                <MenuItem key="edit" disabled><UpgradeIcon /><Gap5 />{t("Edit Template…")}</MenuItem>,
                <MenuItem key="settings" disabled><UpgradeIcon /><Gap5 />{t("Properties…")}</MenuItem>,
                <MenuItem key="add-tags" disabled divider><UpgradeIcon /><Gap5 />{t("Add tags…")}</MenuItem>,
                <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
                isPublished && <MenuItem key="unpublish" disabled divider><UpgradeIcon /><Gap5 />{t("Unpublish Template")}</MenuItem>,
                !isPublished && <MenuItem key="publish" disabled divider><UpgradeIcon /><Gap5 />{t("Publish Template")}</MenuItem>,
            ].filter(Boolean) : []),
            ...(!canUpgradeToManageSharedResources && canManageSharedResources ? [
                <MenuItem
                    key="edit"
                    onClick={() => {
                        setContextMenu(null);
                        handleOpenPresentationEditor(record);
                    }}
                >
                    <Icon>edit</Icon>{t("Edit Template…")}
                </MenuItem>,
                <MenuItem
                    key="settings"
                    onClick={() => {
                        setContextMenu(null);
                        handleEditTemplateSettings(record);
                    }}
                >
                    <Icon>label</Icon>{t("Properties…")}
                </MenuItem>,
                <MenuItem
                    key="add-tags"
                    divider
                    onClick={() => {
                        setContextMenu(null);
                        void handleBulkAddTags([record]);
                    }}
                >
                    <Icon>label</Icon>{t("Add tags…")}
                </MenuItem>,
                <MenuItem
                    key="duplicate"
                    divider
                    onClick={() => {
                        setContextMenu(null);
                        void handleDuplicateTemplate(record);
                    }}
                >
                    <Icon>file_copy</Icon>{t("Duplicate")}
                </MenuItem>,
                isPublished ? (
                    <MenuItem
                        key="unpublish"
                        divider
                        onClick={() => {
                            setContextMenu(null);
                            void handleBulkPublish(false, [record]);
                        }}
                    >
                        <Icon>unpublished</Icon>{t("Unpublish Template")}
                    </MenuItem>
                ) : (
                    <MenuItem
                        key="publish"
                        divider
                        onClick={() => {
                            setContextMenu(null);
                            handleBulkPublish(true, [record]);
                        }}
                    >
                        <Icon>cloud_upload</Icon>{t("Publish Template")}
                    </MenuItem>
                ),
            ].filter(Boolean) : []),
            ...(canRemoveFromSelectedFolder ? [
                <MenuItem
                    key="remove-from-folder"
                    divider
                    onClick={() => {
                        setContextMenu(null);
                        handleRemoveFromFolder(record.document.id);
                    }}
                >
                    <Icon>remove_circle</Icon>{t("Remove from Folder")}
                </MenuItem>
            ] : []),
            <MenuItem
                key="delete"
                onClick={() => {
                    setContextMenu(null);
                    handleDeleteTemplates([record]);
                }}
            >
                <Icon>delete</Icon>{t("Delete Template")}
            </MenuItem>,
        ];
    };

    const handleVisibleChange = useCallback((isVisible: boolean, presentationId: string) => {
        if (!isVisible || fetching || !PresentationTemplatesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(record => record.document.id === presentationId);
        if (index === data.length - 1) {
            void PresentationTemplatesPaneController.loadMore();
        }
    }, [data, fetching]);

    useEffect(() => {
        setSelectedIds(current => current.filter(id => data.some(record => record.document.id === id)));
    }, [getObjectHash(data)]);

    const hasFilter = filters.some(filter =>
        (filter.field !== "resource.metadata.isPublished" && filter.field !== "resource.folderIds")) || !!search.query;
    const hasNoMatch = data.length === 0 && hasFilter;
    const hasNoData = data.length === 0 && !hasFilter;
    const isLoadingMore = fetching && data.length > 0;
    const isLoadingInitial = fetching && data.length === 0;

    if (isLoadingInitial || !PresentationTemplatesPaneController.initialized) {
        return <Spinner />;
    }

    if (hasNoMatch) {
        return <NoMatchNotice />;
    }

    if (hasNoData) {
        if (selectedFolderId) {
            return (<SharedSlideFolderNotice hideMessage={!editable} />);
        }

        return (
            <ListContainer>
                <NoTeamTemplateNotice />
            </ListContainer>
        );
    }

    return (
        <ListContainer>
            <Container ref={listRef} onMouseDown={handleListMouseDown}>
                {data.map(record => (
                    <PresentationTemplateItem
                        key={record.document.id}
                        record={record}
                        editable={editable}
                        onSelect={allowDragging ? handleSelect : handleItemClick}
                        onMouseDown={allowDragging ? event => handleItemMouseDown(record, event) : undefined}
                        onMouseUp={allowDragging ? event => handleItemMouseUp(record, event) : undefined}
                        onMenuClick={allowDragging ? handleItemMenuClick : undefined}
                        onDuplicate={() => void handleDuplicateTemplate(record)}
                        onAddTags={() => void handleBulkAddTags([record])}
                        onPublish={() => void handleBulkPublish(true, [record])}
                        onUnpublish={() => void handleBulkPublish(false, [record])}
                        onDelete={() => void handleDeleteTemplates([record])}
                        onContextMenu={editable ? event => handleItemContextMenu(record, event) : undefined}
                        selected={selectedIds.includes(record.document.id)}
                        getPresentationSlidesMetadata={handleGetPresentationSlidesMetadata}
                        onVisibleChange={handleVisibleChange}
                        selectedFolderId={selectedFolderId}
                        onRemoveFromFolder={canRemoveFromSelectedFolder ? handleRemoveFromFolder : undefined}
                        resourceFolders={resourceFolders}
                        onNavigateToFolder={onNavigateToFolder}
                    />
                ))}
                {isLoadingMore && <PresentationTemplateItemSkeleton />}
                {contextMenu && (
                    <ContextMenu
                        x={contextMenu.mouseX}
                        y={contextMenu.mouseY}
                        onCancel={() => setContextMenu(null)}
                    >
                        {renderContextMenuItems()}
                    </ContextMenu>
                )}
            </Container>
            {editable && selectedIds.length > 0 && (
                <BulkActionBar
                    selectedCount={selectedIds.length}
                    onDelete={() => handleDeleteTemplates()}
                    onClearSelection={() => setSelectedIds([])}
                >
                    <BulkActionButton
                        data-testid="bulk-action-add-tags"
                        icon="label"
                        onClick={() => handleBulkAddTags()}
                    >
                        {t("Add Tags…")}
                    </BulkActionButton>

                    {hasUnpublished && (
                        <BulkActionButton
                            data-testid="bulk-action-publish"
                            icon="check_circle"
                            onClick={() => handleBulkPublish(true)}
                        >
                            {t("Publish")}
                        </BulkActionButton>
                    )}

                    {hasPublished && (
                        <BulkActionButton
                            data-testid="bulk-action-unpublish"
                            icon="unpublished"
                            onClick={() => handleBulkPublish(false)}
                        >
                            {t("Unpublish")}
                        </BulkActionButton>
                    )}
                </BulkActionBar>
            )}
        </ListContainer>
    );
}

export default PresentationTemplatesPaneController.withState(PresentationTemplateList);
